import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { BehaviorSubject, catchError, finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  CreateScanRequest,
  CreateScanResponse,
  ScanByIdResponse,
  ScansClient,
  SearchScanResultsRequest,
  SearchScanResultsResponsePagedList,
  SearchScansRequest,
} from '../../core/openapi/bff-admin-api';
import { ScannerMapper } from './scanner.mapper';
import { PagedList } from '../../shared/types/paged-list.interface';
import { CreateScan, ScanById, SearchScanResult, SearchScans } from './scanner.model';
import { ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { outputError } from '../../shared/utils/output-error.function';
import { LoadingState } from '../../shared/types/loading.type';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/const/pagination.constants';

@Injectable()
export class ScannerService {
  private readonly scansClient = inject(ScansClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private readonly _scannerTotalCount: WritableSignal<number> = signal(0);
  private readonly _scannerLoadingStatus: WritableSignal<LoadingState> = signal(
    LoadingState.LOADING,
  );

  protected readonly scannerRequest$ = new BehaviorSubject<SearchScansRequest>({
    skip: 0,
    take: DEFAULT_TABLE_PAGE_SIZE,
    includeTotalCount: true,
  });

  public readonly scannerLoadingStatus = this._scannerLoadingStatus.asReadonly();
  public readonly scannerTotalCount = this._scannerTotalCount.asReadonly();

  public getScanById(scanId: string): Observable<ScanById> {
    return this.scansClient
      .v1ScansIdGet(scanId)
      .pipe(map((res: ScanByIdResponse) => ScannerMapper.mapGetScanByIdResponse(res)));
  }

  public createScan(request: CreateScanRequest): Observable<CreateScan> {
    return this.scansClient
      .v1ScansPost(request)
      .pipe(map((res: CreateScanResponse) => ScannerMapper.mapCreateScanFromResponse(res)));
  }

  private getScans$: Observable<PagedList<SearchScans>> = this.scannerRequest$.pipe(
    tap(() => this._scannerLoadingStatus.set(LoadingState.LOADING)),
    switchMap((params) =>
      this.scansClient.v1ScansSearchPost(params).pipe(
        catchError((error) => {
          this.toastService.error({
            message: this.translateService.instant(
              'monitoring.scanner.toast.scanner-fetching-failed',
              {
                error: outputError(error, this.translateService.instant('errors.unknown')),
              },
            ),
          });

          this._scannerTotalCount.set(0);
          return of({ items: [], totalCount: 0 });
        }),
        finalize(() => this._scannerLoadingStatus.set(LoadingState.READY)),
      ),
    ),
    map(
      (res): PagedList<SearchScans> => ({
        items: (res.items ?? []).map(ScannerMapper.mapSearchScansFromResponse),
        totalCount: res.totalCount ?? 0,
      }),
    ),
    tap(({ totalCount }) => {
      this._scannerTotalCount.set(totalCount);
    }),
  );

  public scans$ = this.getScans$.pipe(map(({ items }) => items));

  public searchScansResults(
    params: SearchScanResultsRequest,
  ): Observable<PagedList<SearchScanResult>> {
    return this.scansClient.v1ScansSearchResultsPost(params).pipe(
      map((res: SearchScanResultsResponsePagedList): PagedList<SearchScanResult> => {
        return {
          items: (res.items || []).map(ScannerMapper.mapSearchScanResultsFromResponse),
          totalCount: res.totalCount ?? 0,
        };
      }),
    );
  }

  public approveScanResult(scanId: string): Observable<void> {
    return this.scansClient.v1ScansIdApprovePost(scanId);
  }

  public cancelScan(scanId: string): Observable<void> {
    return this.scansClient.v1ScansIdCancelPost(scanId);
  }

  public setScanResultModel(scanResultId: string, equipmentModelId: string): Observable<void> {
    return this.scansClient.v1ScansScanResultsScanResultIdSetModelPost(scanResultId, {
      equipmentModelId,
    });
  }

  public updateScannerRequest(params: Partial<SearchScansRequest>): void {
    this.scannerRequest$.next({
      ...this.scannerRequest$.value,
      ...params,
    });
  }

  public refresh(): void {
    this.scannerRequest$.next({ ...this.scannerRequest$.value });
  }
}
